<template>
  <div class="gfr">
    <!-- ── Hero ── -->
    <page-hero
        class="mb-1"
        :title="msg('Guest features')"
        :subtitle="msg('guestFeaturesSubtitle')"
        variant="contract"
        :stats="[{ icon: 'GiftIcon', value: page.total, label: msg('total') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="40" width="60" height="50" rx="6" fill="rgba(255,255,255,.16)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
          <path d="M20 54h60" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
          <rect x="44" y="40" width="12" height="50" fill="rgba(255,255,255,.18)"/>
          <path d="M50 40c-8-14-26-8-20 2M50 40c8-14 26-8 20 2" stroke="rgba(255,255,255,.35)" stroke-width="2" fill="none"/>
          <circle cx="82" cy="28" r="8" fill="rgba(255,255,255,.08)"/>
          <circle cx="12" cy="95" r="10" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <!-- ── Filter workspace ── -->
    <div class="gfr-filter">
      <div class="gfr-filter__head">
        <span class="gfr-filter__badge"><i class="fas fa-sliders-h"/></span>
        <span class="gfr-filter__title">{{ msg('Filters') }}</span>
        <span class="gfr-filter__line"/>
        <button type="button" class="gfr-icon-btn" v-b-tooltip.hover :title="msg('Clear all')" @click="clearFilters">
          <i class="fas fa-eraser"/>
        </button>
        <button type="button" class="gfr-icon-btn" v-b-tooltip.hover :title="msg('Reload')" @click="refresh()">
          <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
        </button>
      </div>

      <!-- status segments: the workflow states, one click away -->
      <div class="gfr-segments">
        <button v-for="segment in segments" :key="segment.key" type="button"
                class="gfr-segment" :class="{'gfr-segment--on': statusSegment === segment.key, [`gfr-segment--${segment.tone}`]: true}"
                @click="setSegment(segment.key)">
          <i :class="segment.icon" class="mr-25"/>{{ segment.label }}
        </button>
      </div>

      <div class="gfr-grid">
        <div class="gfr-field">
          <label class="gfr-label"><i class="fas fa-search mr-25"/>{{ msg('Search') }}</label>
          <b-input-group size="sm" class="gfr-group">
            <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
            <b-form-input v-model="quickSearch" :placeholder="$t('analyticsHub.searchGuest')" debounce="150"/>
          </b-input-group>
        </div>
        <div class="gfr-field gfr-field--wide">
          <label class="gfr-label"><i class="far fa-calendar-alt mr-25"/>{{ msg('checkinrange') }}</label>
          <calendar-picker-range size="sm" v-model="filter.checkInDate"/>
        </div>
        <div class="gfr-field gfr-field--wide">
          <label class="gfr-label"><i class="fas fa-home mr-25"/>{{ $t('Real estate') }}</label>
          <real-estate-picker small v-model="filter.realEstatesId" multiple/>
        </div>
        <div class="gfr-field gfr-field--toggles">
          <label class="gfr-label"><i class="fas fa-toggle-on mr-25"/>{{ msg('Status') }}</label>
          <div class="gfr-toggles">
            <button v-for="toggle in toggles" :key="toggle.key" type="button"
                    class="gfr-toggle" :class="{'gfr-toggle--on': filter[toggle.key] === true}"
                    @click="flipToggle(toggle.key)">
              <i :class="toggle.icon" class="mr-25"/>{{ toggle.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Requests ── -->
    <div class="gfr-panel">
      <div class="gfr-panel__head">
        <span>
          {{ msg('Guest features') }}
          <b-badge variant="light-primary" pill class="ml-50">{{ visibleFeatures.length }} / {{ page.total }}</b-badge>
        </span>
        <div class="gfr-panel__tools">
          <label class="gfr-label mb-0 mr-50">{{ msg('Show') }}</label>
          <v-select v-model="page.amount" @option:selected="refresh" :dir="isRTL ? 'rtl' : 'ltr'"
                    :options="[10, 25, 50, 100, 500]" :clearable="false" class="gfr-page-select"/>
        </div>
      </div>

      <b-table
          ref="feature-table"
          responsive
          v-bind:sort-by.sync="sort.by"
          v-bind:sort-direction="sort.direction"
          v-bind:sort-desc.sync="sort.desc"
          v-bind:items="visibleFeatures"
          v-bind:fields="fields"
          :busy="loading"
          @sort-changed="onSort"
          class="gfr-table"
          thead-class="gfr-thead"
      >
        <template #table-busy>
          <div class="text-center text-primary my-3">
            <b-spinner class="align-middle mx-1"></b-spinner>
            <strong>{{ msg('Loading') }}...</strong>
          </div>
        </template>

        <!-- product: image + name + origin + notes -->
        <template #cell(featureId)="data">
          <div class="gfr-product" @click="openFeatureModal(data.item.id)">
            <div class="gfr-product__img">
              <b-img v-if="productImage(data.item)" :src="productImage(data.item)" fluid/>
              <i v-else class="fa-solid fa-gift"/>
            </div>
            <div class="gfr-product__mid">
              <div class="gfr-product__name">
                {{ data.item.name }}
                <i v-if="data.item.clientNote" class="far fa-comment-dots gfr-note ml-25"
                   v-b-tooltip.hover :title="data.item.clientNote"/>
              </div>
              <div class="gfr-product__sub">
                <span v-if="data.item.clientRequest" class="gfr-origin gfr-origin--guest">
                  <i class="fa-solid fa-route mr-25"/>{{ $t('clientRequest') }}
                </span>
                <span v-else class="gfr-origin">
                  <i class="fa-solid fa-user-tie mr-25"/>{{ msg('Staff') }}</span>
                <span v-if="data.item.dailyPayment" class="gfr-origin"><i class="far fa-clock mr-25"/>{{ data.item.days }}d</span>
              </div>
            </div>
          </div>
        </template>

        <!-- guest: avatar + name + verified + contract link -->
        <template #cell(contractId)="data">
          <div class="gfr-guest">
            <span class="gfr-avatar" :class="data.item.contractVerified ? 'gfr-avatar--ok' : 'gfr-avatar--plain'">{{ initials(data.item.clientName) }}</span>
            <div class="gfr-guest__mid">
              <div class="gfr-guest__name">
                {{ data.item.clientName || '—' }}
                <i v-if="data.item.contractVerified" class="fa-solid fa-shield-halved gfr-shield ml-25"
                   v-b-tooltip.hover :title="$t('Verified')"/>
              </div>
              <button type="button" class="gfr-link" @click.stop="openContract(data.item)">
                <i class="fa-solid fa-arrow-up-right-from-square mr-25"/>{{ $t('contract') }}
              </button>
            </div>
          </div>
        </template>

        <template #cell(fullAddress)="data">
          <div class="gfr-address" v-b-tooltip.hover :title="data.value">{{ data.value || '—' }}</div>
        </template>

        <template #cell(checkInDate)="data">
          <div class="gfr-dates">
            <span class="gfr-dates__main"><i class="fas fa-sign-in-alt mr-25"/>{{ data.value | moment('DD.MM.YY') }}</span>
            <span class="gfr-dates__sub">{{ msg('requestTime') }}: {{ data.item.requestTime | moment('DD.MM.YY HH:mm') }}</span>
          </div>
        </template>

        <!-- money: qty × price = total -->
        <template #cell(finalPrice)="data">
          <div class="gfr-money">
            <span class="gfr-money__total tabular">{{ data.item.finalPrice | numberFormatter }} {{ data.item.currency || '€' }}</span>
            <span v-if="data.item.finalAmount > 1" class="gfr-money__calc tabular">{{ data.item.finalAmount }} × {{ data.item.price | numberFormatter }}</span>
          </div>
        </template>

        <!-- lifecycle: status + approval + payments -->
        <template #cell(status)="data">
          <div class="gfr-state">
            <span class="gfr-pill" :class="`gfr-pill--${String(data.value).toLowerCase()}`">
              <i :class="statusIcon(data.value)" class="mr-25"/>{{ $t(`guestFeatureStatusEnum.${data.value}`) }}
            </span>
            <span v-if="data.item.status === 'REQUESTED'" class="gfr-pill" :class="data.item.approved ? 'gfr-pill--approved' : 'gfr-pill--waiting'">
              <i :class="data.item.approved ? 'fa-solid fa-check' : 'far fa-hourglass'" class="mr-25"/>
              {{ data.item.approved ? $t('approved') : msg('Awaiting approval') }}
            </span>
            <span v-if="data.item.payments && data.item.payments.length" class="gfr-pill gfr-pill--payments"
                  @click.stop="togglePaymentDropdown(data.item)">
              <i class="far fa-credit-card mr-25"/>{{ data.item.payments.length }}
            </span>
            <transition name="fade">
              <div v-if="data.item._showPayments" class="payment-dropdown">
                <div v-for="p in data.item.payments" :key="p.id" class="payment-item">
                  <div class="d-flex justify-content-between align-items-center mb-25">
                    <span class="small">{{ p.paymentDate | moment($t('timeFormat.dateTime.format')) }}</span>
                    <b-badge :variant="p.paid ? 'light-success' : 'light-danger'">{{ p.status }}</b-badge>
                  </div>
                  <div class="small text-muted">{{ cleanReason(p.reason) }}</div>
                  <div class="font-weight-bolder tabular">{{ p.amount | numberFormatter }} {{ p.currency }}</div>
                  <hr v-if="data.item.payments.length > 1"/>
                </div>
              </div>
            </transition>
          </div>
        </template>

        <!-- actions -->
        <template #cell(actions)="data">
          <div class="gfr-actions">
            <button v-if="canPay(data.item)" type="button" class="gfr-action gfr-action--pay"
                    v-b-tooltip.hover :title="$t('Pay orders in Contract')" @click.stop="openPayModal(data.item)">
              <i class="fa-solid fa-cash-register"/>
            </button>
            <button type="button" class="gfr-action" v-b-tooltip.hover :title="msg('Edit')"
                    @click.stop="openFeatureModal(data.item.id)">
              <i class="far fa-pen-to-square"/>
            </button>
            <button type="button" class="gfr-action" v-b-tooltip.hover :title="$t('View Contract')"
                    @click.stop="openContract(data.item)">
              <i class="fa-solid fa-file-contract"/>
            </button>
          </div>
        </template>
      </b-table>

      <div v-if="!loading && !visibleFeatures.length" class="gfr-empty">
        <i class="fa-solid fa-gift"/>
        <p>{{ msg('nodatafound') }}</p>
      </div>

      <div class="gfr-foot">
        <span class="gfr-foot__total"><strong>{{ msg('Total') }}:</strong> {{ page.total }}</span>
        <b-pagination
            v-model="page.currentPage"
            :total-rows="page.total"
            :per-page="page.amount"
            first-number
            last-number
            class="mb-0"
            @input="refresh"
        />
      </div>
    </div>

    <guest-feature-requested-modal ref="guest-feature-modal" @update="refresh"/>
    <guest-feature-order-modal ref="guest-feature-order-modal" @update="refresh"/>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import PageHero from "@/components/PageHero.vue";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker.vue";
import GuestFeatureRequestedModal from "@/components/modals/feature/GuestFeatureRequestedModal.vue";
import GuestFeatureOrderModal from "@/components/modals/feature/GuestFeatureOrderModal.vue";
import CalendarPickerRange from "@/components/widgets/calendar/calendarPickerRange.vue";

const createGuestFeatureRequest = () => {
  return {
    featuresId: null, realEstatesId: null, minPrice: null, statuses: null, noPayments: null,
    clientRequest: null, approved: null, contractVerified: null,
    checkInDate: {
      start: null,
      end: null,
    }
  };
}

export default {
  name: "guestFeatureRequest",
  components: {PageHero, CalendarPickerRange, GuestFeatureRequestedModal, RealEstatePicker, GuestFeatureOrderModal},
  data: () => ({
    loading: true,
    filter: {},
    quickSearch: '',
    statusSegment: 'ALL',
    sort: {by: 'requestTime', direction: 'desc', desc: true},
    features: [],
    products: [],
    page: {amount: 25, currentPage: 1, total: 0},
  }),
  created() {
    this.filter = createGuestFeatureRequest();
    this.refresh();
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.refresh();
      }
    },
  },
  computed: {
    segments() {
      return [
        {key: 'ALL', label: this.msg('All'), icon: 'fas fa-layer-group', tone: 'all'},
        {key: 'REQUESTED', label: this.$t('guestFeatureStatusEnum.REQUESTED'), icon: 'fas fa-hourglass-half', tone: 'requested'},
        {key: 'PAID', label: this.$t('guestFeatureStatusEnum.PAID'), icon: 'fas fa-check-circle', tone: 'paid'},
        {key: 'CANCELED', label: this.$t('guestFeatureStatusEnum.CANCELED'), icon: 'fas fa-times-circle', tone: 'canceled'},
      ]
    },
    toggles() {
      return [
        {key: 'approved', label: this.$t('approved'), icon: 'fa-solid fa-check'},
        {key: 'clientRequest', label: this.$t('clientRequest'), icon: 'fa-solid fa-route'},
        {key: 'contractVerified', label: this.$t('Verified'), icon: 'fa-solid fa-shield-halved'},
        {key: 'noPayments', label: this.$t('No payment attends'), icon: 'far fa-credit-card'},
      ]
    },
    /** quick search filters the loaded rows instantly — guest, product or address */
    visibleFeatures() {
      const term = (this.quickSearch || '').trim().toLowerCase()
      if (!term) return this.features
      return this.features.filter(feature =>
        [feature.clientName, feature.name, feature.fullAddress]
          .some(value => String(value || '').toLowerCase().includes(term)))
    },
    fields() {
      return [
        {key: 'featureId', label: this.$t('feature'), sortable: true, thClass: 'gfr-th', tdClass: 'gfr-td'},
        {key: 'contractId', label: this.$t('contract'), sortable: true, thClass: 'gfr-th', tdClass: 'gfr-td'},
        {key: 'fullAddress', label: this.$t('address'), sortable: true, thClass: 'gfr-th', tdClass: 'gfr-td'},
        {key: 'checkInDate', label: this.$t('Check In'), sortable: true, thClass: 'gfr-th', tdClass: 'gfr-td'},
        {key: 'finalPrice', label: this.$t('price'), sortable: false, thClass: 'gfr-th text-right', tdClass: 'gfr-td text-right'},
        {key: 'status', label: this.$t('status'), sortable: false, thClass: 'gfr-th', tdClass: 'gfr-td'},
        {key: 'actions', label: '', sortable: false, thClass: 'gfr-th', tdClass: 'gfr-td text-right'},
      ]
    },
    productsMapById() {
      if (!Array.isArray(this.products)) {
        return {}
      }
      return this.products.reduce((map, product) => {
        map[product.id] = product
        return map
      }, {})
    }
  },
  methods: {
    ...mapActions('guestFeatureRequest', ['fetchAllGuestFeatures']),
    initials(name) {
      if (!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()
    },
    productImage(feature) {
      const product = this.productsMapById[feature.featureId]
      return product != null ? product.imageUri : null
    },
    setSegment(key) {
      this.statusSegment = key
      // writing filter.statuses triggers the deep watcher → refresh
      this.$set(this.filter, 'statuses', key === 'ALL' ? null : [key])
    },
    /** on → true; off → null (NOT false — a stale false used to silently filter) */
    flipToggle(key) {
      this.$set(this.filter, key, this.filter[key] === true ? null : true)
    },
    clearFilters() {
      this.statusSegment = 'ALL'
      this.quickSearch = ''
      this.filter = createGuestFeatureRequest()
    },
    cleanReason(reason) {
      if (!reason) return "";
      const parts = reason.split("-");
      return parts.length > 1 ? parts[1].trim() : reason;
    },
    togglePaymentDropdown(feature) {
      if (feature._showPayments === undefined) {
        this.$set(feature, "_showPayments", true);
      } else {
        feature._showPayments = !feature._showPayments;
      }
    },
    refresh() {
      let $this = this;
      this.loading = true;

      let request = {
        filter: this.filter,     // contractVerified is ignored by backend
        page: this.page.currentPage,
        amount: this.page.amount,
        sortcolumn: $this.sort.by,
        sortby: $this.sort.direction
      };

      this.fetchAllGuestFeatures(request).then(data => {
        let items = data.features;
        // the products catalog carries the images — it was fetched but never stored before
        $this.products = data.products || [];

        // client-side extras the backend does not filter
        const clientFiltered = $this.filter.contractVerified === true || $this.filter.noPayments === true;
        if ($this.filter.contractVerified === true) {
          items = items.filter(f => f.contractVerified === true);
        }
        if ($this.filter.noPayments === true) {
          items = items.filter(f => !f.payments || f.payments.length === 0);
        }

        $this.features = items;
        // server total drives the pagination; only the extra client-side
        // filters (which the backend cannot apply) shrink it to the page
        $this.page.total = clientFiltered ? items.length : (data.TotalRows || items.length);
      }).finally(() => {
        $this.loading = false;
      })
    },
    statusIcon(status) {
      return {
        PAID: "fas fa-check-circle",
        REQUESTED: "fas fa-hourglass-half",
        CANCELED: "fas fa-times-circle",
      }[status] || "fas fa-info-circle";
    },
    openContract(feature) {
      window.open(`/real_estate/contracts/rent/${feature.contractId}#guest-feature`, '_blank');
    },
    openPayModal(feature) {
      this.$refs['guest-feature-order-modal'].show(feature.contractId);
    },
    openFeatureModal(featureId) {
      this.$refs['guest-feature-modal'].show(featureId);
    },
    canPay(feature) {
      return feature.status === "REQUESTED" && feature.approved === true;
    },
    onSort(data) {
      this.sort.by = data.sortBy
      this.sort.direction = data.sortDesc ? 'desc' : 'asc'
      this.refresh()
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/* ── filter workspace ── */
.gfr-filter {
  background: #fff; border: 1px solid rgba(15, 23, 42, .06); border-radius: 16px;
  padding: 14px 18px; margin-bottom: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, .04);
}
.gfr-filter__head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.gfr-filter__badge {
  width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(194, 170, 142, .22); color: #a9863f; font-size: .8rem;
}
.gfr-filter__title { font-size: .82rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #475569; }
.gfr-filter__line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(100, 116, 139, .18), transparent); }
.gfr-icon-btn {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(15, 23, 42, .05); color: #64748b; font-size: .74rem;
  transition: background .15s, color .15s;
  &:hover { background: rgba(115, 103, 240, .14); color: #7367f0; }
  &:focus { outline: none; }
}

/* status segments */
.gfr-segments { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.gfr-segment {
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 999px; background: transparent;
  color: #64748b; font-size: .76rem; font-weight: 700; cursor: pointer;
  padding: 6px 14px; transition: all .15s;
  &:focus { outline: none; }
  &:hover { border-color: rgba(115, 103, 240, .5); color: #7367f0; }
  &--on.gfr-segment--all { background: #7367f0; border-color: #7367f0; color: #fff; }
  &--on.gfr-segment--requested { background: #ff9f43; border-color: #ff9f43; color: #fff; }
  &--on.gfr-segment--paid { background: #28c76f; border-color: #28c76f; color: #fff; }
  &--on.gfr-segment--canceled { background: #ea5455; border-color: #ea5455; color: #fff; }
}

/* filter grid — ONE row on desktop; fields shrink before they wrap */
.gfr-grid {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(210px, 1.3fr) minmax(180px, 1.2fr) minmax(260px, 1.7fr);
  gap: 12px 14px; align-items: end;
}
.gfr-grid :deep(.form-control), .gfr-grid :deep(.vs__dropdown-toggle) { min-width: 0; }
@media (max-width: 1100px) { .gfr-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 640px) { .gfr-grid { grid-template-columns: 1fr; } }
.gfr-field { min-width: 0; }
.gfr-label {
  display: flex; align-items: center;
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
  color: #94a3b8; margin-bottom: 5px;
  i { opacity: .7; }
}
.gfr-field :deep(.form-control) { height: 34px; }
.gfr-group :deep(.input-group-text) { height: 34px; background: rgba(15, 23, 42, .03); border-color: rgba(15, 23, 42, .1); color: #94a3b8; font-size: .7rem; }
.gfr-field :deep(.vs__dropdown-toggle) { min-height: 34px; border-color: rgba(15, 23, 42, .1); }
/* the date-range picker builds its own trigger pills — same 34px baseline */
.gfr-field :deep(.cal-range-picker__row) { margin-bottom: 0; }
.gfr-field :deep(.cal-range-picker__field) { height: 34px; padding: 0 12px; }

/* toggle chips */
.gfr-toggles { display: flex; flex-wrap: wrap; gap: 6px; }
.gfr-toggle {
  border: 1px dashed rgba(15, 23, 42, .18); border-radius: 999px; background: transparent;
  color: #64748b; font-size: .72rem; font-weight: 700; cursor: pointer;
  padding: 6px 12px; transition: all .15s; white-space: nowrap;
  &:focus { outline: none; }
  &:hover { border-color: rgba(115, 103, 240, .5); color: #7367f0; }
  &--on { background: rgba(115, 103, 240, .12); border: 1px solid #7367f0; color: #7367f0; }
}

/* ── panel & table ── */
.gfr-panel {
  background: #fff; border: 1px solid rgba(15, 23, 42, .06); border-radius: 16px;
  padding: 14px 18px 12px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, .04);
}
.gfr-panel__head {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
  font-size: .9rem; font-weight: 800; color: #334155; margin-bottom: 10px;
}
.gfr-panel__tools { display: flex; align-items: center; }
.gfr-page-select { min-width: 86px; }

.gfr-table { font-size: .85rem; }
.gfr-table :deep(.gfr-th) {
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
  color: #94a3b8; background: rgba(15, 23, 42, .02); border-bottom: 1px solid rgba(15, 23, 42, .07) !important;
}
.gfr-table :deep(.gfr-td) { vertical-align: middle; padding: 10px 12px; border-top: 1px solid rgba(15, 23, 42, .05); }
.gfr-table :deep(tbody tr:hover) { background: rgba(115, 103, 240, .04); }

/* product cell */
.gfr-product { display: flex; align-items: center; gap: 10px; cursor: pointer; min-width: 200px; }
.gfr-product__img {
  width: 44px; height: 44px; border-radius: 11px; flex-shrink: 0; overflow: hidden;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(194, 170, 142, .18); color: #a9863f; font-size: 1rem;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.gfr-product__name { font-weight: 800; color: #1f2a44; white-space: nowrap; }
.gfr-product__sub { display: flex; gap: 6px; margin-top: 2px; }
.gfr-note { color: #ff9f43; cursor: help; }
.gfr-origin {
  display: inline-flex; align-items: center;
  font-size: .64rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #94a3b8;
  &--guest { color: #00a5b5; }
}

/* guest cell */
.gfr-guest { display: flex; align-items: center; gap: 9px; min-width: 150px; }
.gfr-avatar {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: .66rem; font-weight: 800; color: #fff;
  &--ok { background: linear-gradient(135deg, #34d399, #059669); }
  &--plain { background: linear-gradient(135deg, #94a3b8, #64748b); }
}
.gfr-guest__name { font-weight: 700; color: #1f2a44; white-space: nowrap; text-transform: capitalize; }
.gfr-shield { color: #28c76f; font-size: .7rem; }
.gfr-link {
  border: none; background: none; padding: 0; cursor: pointer;
  font-size: .68rem; font-weight: 700; color: #7367f0;
  &:hover { text-decoration: underline; }
  &:focus { outline: none; }
}

.gfr-address { max-width: 190px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #64748b; font-size: .78rem; }

.gfr-dates { display: flex; flex-direction: column; white-space: nowrap; }
.gfr-dates__main { font-weight: 700; color: #1f2a44; font-size: .8rem; i { color: #28c76f; } }
.gfr-dates__sub { font-size: .66rem; color: #94a3b8; margin-top: 2px; }

.gfr-money { display: flex; flex-direction: column; align-items: flex-end; white-space: nowrap; }
.gfr-money__total { font-weight: 800; color: #1f2a44; }
.gfr-money__calc { font-size: .68rem; color: #94a3b8; }

/* lifecycle pills */
.gfr-state { position: relative; display: flex; flex-wrap: wrap; gap: 4px; min-width: 170px; }
.gfr-pill {
  display: inline-flex; align-items: center;
  font-size: .66rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 999px; white-space: nowrap;
  &--paid { background: rgba(40, 199, 111, .13); color: #1f9d57; }
  &--requested { background: rgba(255, 159, 67, .15); color: #b26a12; }
  &--canceled { background: rgba(234, 84, 85, .13); color: #c62a2b; }
  &--approved { background: rgba(115, 103, 240, .12); color: #7367f0; }
  &--waiting { background: rgba(100, 116, 139, .12); color: #64748b; }
  &--payments { background: rgba(0, 207, 232, .12); color: #0090a5; cursor: pointer; }
}

/* actions */
.gfr-actions { display: inline-flex; gap: 6px; }
.gfr-action {
  width: 30px; height: 30px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; color: #64748b; font-size: .74rem;
  transition: all .15s;
  &:hover { border-color: #7367f0; color: #7367f0; background: rgba(115, 103, 240, .08); }
  &:focus { outline: none; }
  &--pay { border-color: rgba(40, 199, 111, .4); color: #1f9d57; background: rgba(40, 199, 111, .08);
    &:hover { background: #28c76f; border-color: #28c76f; color: #fff; } }
}

/* empty + footer */
.gfr-empty { text-align: center; padding: 40px 0 26px; color: #94a3b8; i { font-size: 2rem; opacity: .4; } p { margin-top: 8px; font-size: .85rem; } }
.gfr-foot { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; padding-top: 12px; border-top: 1px solid rgba(15, 23, 42, .06); }
.gfr-foot__total { font-size: .8rem; color: #64748b; }

/* payments dropdown (restyled) */
.payment-dropdown {
  position: absolute; top: 28px; right: 0; left: auto; z-index: 9999;
  background: #fff; border: 1px solid rgba(15, 23, 42, .1); border-radius: 12px;
  padding: 12px; width: 280px; max-height: 220px; overflow-y: auto;
  box-shadow: 0 16px 40px -12px rgba(15, 23, 42, .3);
}
.payment-item { margin-bottom: 6px; }
.payment-item hr { margin: 6px 0; opacity: .2; }
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>

<style lang="scss">
.dark-layout {
  .gfr-filter, .gfr-panel { background: #283046; border-color: rgba(255, 255, 255, .07); box-shadow: 0 2px 10px rgba(0, 0, 0, .2); }
  .gfr-filter__title, .gfr-panel__head { color: #d0d2d6; }
  .gfr-filter__line { background: linear-gradient(90deg, rgba(255, 255, 255, .12), transparent); }
  .gfr-icon-btn { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .gfr-label { color: #8a92a6; }
  .gfr-group .input-group-text { background: rgba(255, 255, 255, .04); border-color: rgba(255, 255, 255, .12); color: #8a92a6; }
  .gfr-segment, .gfr-toggle { border-color: rgba(255, 255, 255, .16); color: #94a3b8; }
  .gfr-table .gfr-th { background: rgba(255, 255, 255, .03); color: #8a92a6; border-color: rgba(255, 255, 255, .08) !important; }
  .gfr-table .gfr-td { border-color: rgba(255, 255, 255, .06); }
  .gfr-table tbody tr:hover { background: rgba(115, 103, 240, .1); }
  .gfr-product__name, .gfr-guest__name, .gfr-dates__main, .gfr-money__total { color: #e9ecf2; }
  .gfr-address { color: #94a3b8; }
  .gfr-action { border-color: rgba(255, 255, 255, .12); color: #94a3b8; }
  .gfr-foot { border-color: rgba(255, 255, 255, .08); }
  .payment-dropdown { background: #283046; border-color: rgba(255, 255, 255, .12); }
}
</style>
